import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { useDiscounts } from '../../queries/discounts';
import { MarketingOffer, useDeleteMarketingOffer, useMarketingOffers } from '../../queries/marketingOffers';
import { useSubscriptionPackages } from '../../queries/subscriptionPackages';
import { OfferFormDialog } from './OfferFormDialog';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

export function OffersPage() {
  const { data: offers = [], isLoading } = useMarketingOffers();
  const { data: packages = [] } = useSubscriptionPackages();
  const { data: discounts = [] } = useDiscounts();
  const remove = useDeleteMarketingOffer();
  const { success, error } = useToast();

  const [editingOffer, setEditingOffer] = useState<MarketingOffer | null | 'new'>(null);
  const [deleting, setDeleting] = useState<MarketingOffer | null>(null);

  const confirmDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => {
        success(`Deleted "${deleting.name}".`);
        setDeleting(null);
      },
      onError: (err) => {
        error(extractErrorMessage(err, 'Delete failed.'));
        setDeleting(null);
      },
    });
  };

  const columns: DataTableColumn<MarketingOffer>[] = [
    { key: 'name', header: 'Name', priority: true, render: (o) => <span className="font-medium">{o.name}</span> },
    { key: 'package', header: 'Package', priority: false, render: (o) => <span className="text-muted-foreground @2xl:text-foreground">{o.package?.name ?? o.package_code ?? 'Any package'}</span> },
    { key: 'type', header: 'Type', priority: false, render: (o) => <span className="text-muted-foreground @2xl:text-foreground">{o.offer_type === 'FREE_GRANT' ? 'Free grant' : 'Stripe discount'}</span> },
    { key: 'active', header: 'Active', priority: true, render: (o) => (o.is_active ? <Badge variant="success">Active</Badge> : <Badge variant="outline">Inactive</Badge>) },
    { key: 'campaigns', header: 'Campaigns', priority: false, alignEnd: true, render: (o) => <span className="tabular-nums text-muted-foreground @2xl:text-foreground">{o._count.campaigns}</span> },
    {
      key: 'actions',
      header: '',
      priority: true,
      alignEnd: true,
      render: (o) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => setEditingOffer(o)} title="Edit"><Pencil className="h-4 w-4" /></Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeleting(o)}
            disabled={o._count.campaigns > 0}
            title={o._count.campaigns > 0 ? 'Cannot delete — used by a campaign' : 'Delete'}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Reusable grant/discount definitions campaigns are built on.</p>
        <Button onClick={() => setEditingOffer('new')}>
          <Plus className="h-4 w-4" />
          Create offer
        </Button>
      </div>

      {isLoading ? <Spinner /> : <DataTable columns={columns} data={offers} getRowKey={(o) => String(o.id)} emptyMessage="No offers yet." />}

      {editingOffer && (
        <OfferFormDialog offer={editingOffer === 'new' ? null : editingOffer} packages={packages} discounts={discounts} onClose={() => setEditingOffer(null)} />
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete offer?"
        description={`"${deleting?.name}" will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
